import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  MessageSquare, Megaphone, PhoneCall, Star, Award, BarChart2, 
  ArrowRight, ChevronRight, CheckCircle2, HeartHandshake, 
  Users, Sparkles, TrendingUp
} from 'lucide-react';
import CustomerEngagementImg from '../../assets/Customer.png'; // Assuming you have an image in assets

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';
const THEME_COLOR = '#8b5cf6'; // Violet theme for Customer Engagement

/* ============================== PAGE DATA ============================== */

const FEATURES = [
  { 
    title: 'Omnichannel Communication', 
    icon: MessageSquare, 
    desc: 'Connect with customers via Email, SMS, WhatsApp, and live chat from a unified inbox.' 
  },
  { 
    title: 'Marketing Automation', 
    icon: Megaphone, 
    desc: 'Trigger personalized email drips, automated campaigns, and targeted push notifications.' 
  },
  { 
    title: 'Helpdesk & Support', 
    icon: PhoneCall, 
    desc: 'Manage support tickets seamlessly with SLA tracking, routing, and automated responses.' 
  },
  { 
    title: 'Feedback & Surveys', 
    icon: Star, 
    desc: 'Gather actionable insights with automated NPS scores, CSAT surveys, and reviews.' 
  },
  { 
    title: 'Loyalty Management', 
    icon: Award, 
    desc: 'Boost retention by building custom loyalty programs, reward tiers, and point systems.' 
  },
  { 
    title: 'Engagement Analytics', 
    icon: BarChart2, 
    desc: 'Track open rates, conversion metrics, and customer sentiment through live dashboards.' 
  }
];

const CustomerEngagement = () => {
  // Use location hook to get routing state
  const location = useLocation();
  const navSource = location.state?.from === 'industries' ? 'industries' : 'products';

  return (
    <div className="overflow-x-hidden pt-0">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        @keyframes seFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        .se-float { animation: seFloat 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .se-float, .se-fade-up { animation: none !important; } }
      `}</style>

      {/* ================= HERO SECTION ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f5f3ff] via-[#faf5ff] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 w-80 h-80 rounded-full bg-[#8b5cf6]/10 blur-3xl" />
        
        <div className={`${CONTAINER} relative py-5 lg:py-6`}>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
            
            {/* LEFT SIDE: Text Content */}
            <div className="max-w-2xl relative z-10 se-fade-up">
              
              {/* Breadcrumb Navigation */}
              <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-6">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <Link to={`/${navSource}`} className="hover:text-primary transition-colors capitalize">{navSource}</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Customer Engagement</span>
              </nav>

              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-primary bg-white ring-1 ring-primary/25 px-4 py-1.5 rounded-full mb-6">
                <HeartHandshake className="w-4 h-4 text-[#8b5cf6]" /> SmartEdz Audience Solutions
              </span>
              <h1 className="text-4xl sm:text-5xl xl:text-[3.7rem] font-extrabold leading-[1.08] tracking-tight mb-6" style={{ color: '#13294b' }}>
                Build Lasting Customer Relationships
              </h1>
              <p className="text-lg text-slate-500 max-w-xl leading-relaxed mb-8">
                Connect with your audience across every channel. Automate marketing, streamline support, and drive loyalty with our comprehensive customer engagement suite.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-7 py-3.5 font-semibold text-white shadow-lg shadow-primary/30 bg-primary hover:brightness-110 transition">
                  Request a Demo <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* RIGHT SIDE: Image replacing mockups */}
            <div className="relative se-fade-up w-full" style={{ animationDelay: '.15s' }}>
              <div className="absolute inset-x-8 top-4 bottom-16 bg-gradient-to-tr from-[#8b5cf6]/15 to-[#c084fc]/15 blur-3xl rounded-[48px]" />
              <img 
                src={CustomerEngagementImg} 
                alt="Customer Engagement Dashboard Illustration" 
                className="relative z-10 w-full h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-500"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ================= CORE MODULES ================= */}
      <section className="bg-gradient-to-b from-[#f5f9fd] to-white py-14">
        <div className={CONTAINER}>
          <div className="text-center mb-10">
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">Core Modules</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Everything you need in one dashboard</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <div key={index} className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${THEME_COLOR}1A` }}>
                  <feature.icon className="w-6 h-6" style={{ color: THEME_COLOR }} />
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-3">{feature.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INDUSTRY BENEFITS ================= */}
      <section className="py-14 bg-white">
        <div className={CONTAINER}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="se-fade-up">
              <span className="block text-xs font-bold tracking-[0.2em] uppercase text-[#8b5cf6] mb-3">
                Why SmartEdz?
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6" style={{ color: '#13294b' }}>
                Built to maximize retention and lifetime value
              </h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Fragmented communication tools lead to poor customer experiences. Our platform unifies your marketing, support, and feedback data so you can deliver personalized interactions at scale.
              </p>
              <div className="space-y-6">
                {[
                  { title: 'Unified Customer View', desc: 'See every email, chat, ticket, and purchase history in a single, comprehensive timeline for every user.', icon: Users },
                  { title: 'Data-Driven Personalization', desc: 'Use real-time analytics to trigger hyper-personalized messages based on user behavior and preferences.', icon: Sparkles },
                  { title: 'Proactive Support & Retention', desc: 'Identify at-risk customers through sentiment analysis and automate outreach before they churn.', icon: TrendingUp }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-[#8b5cf6]/10 text-[#8b5cf6]">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-lg">{item.title}</h4>
                      <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative rounded-2xl bg-slate-50 border border-slate-100 p-8 shadow-inner se-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#8b5cf6]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
                <HeartHandshake className="w-5 h-5 text-[#8b5cf6]" /> Engagement Health
              </h3>
              <div className="space-y-4 relative z-10">
                {['Omnichannel Active', 'Campaigns Optimized', 'CSAT Improving'].map((label, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center justify-between">
                    <span className="font-semibold text-slate-700 text-sm">{label}</span>
                    <span className="flex items-center gap-1 text-xs font-bold text-[#10b981] bg-[#10b981]/10 px-2 py-1 rounded-md">
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= BOTTOM CTA ================= */}
      <section className="py-16">
        <div className={CONTAINER}>
          <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#13294b] to-[#1e447a] p-10 sm:p-16 text-center">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#8b5cf6]/20 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#8b5cf6]/10 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3" />
            
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
                Ready to delight your customers?
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Join forward-thinking brands who use SmartEdz to automate marketing, resolve tickets faster, and boost customer loyalty.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-8 py-4 font-bold text-[#13294b] bg-white hover:bg-slate-50 transition shadow-lg hover:-translate-y-1">
                  Schedule a Free Demo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-8 py-4 font-bold text-white bg-white/10 hover:bg-white/20 transition ring-1 ring-white/20">
                  Talk to Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomerEngagement;